import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    Transition,
    Combobox,
    DialogPanel,
    DialogTitle,
    ComboboxInput,
    ComboboxButton,
    ComboboxOption, ComboboxOptions
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import ScheduleSection from './ScheduleSection';
import ImagePickerModal from './ImagePickerModal';
import {     type Special, type SpecialMode, type SpecialSchedule, type ProductItem, type CategoryItem,
    type StorageImageItem, } from '../types';
import {
    MIN_SEARCH_CHARS, SEARCH_DEBOUNCE_MS, BUSINESS_TIME_ZONE, IMAGE_PAGE_SIZE,
    fetchProductsFromDb, fetchCategoriesFromDb, fetchBucketImagesFromDb, uploadSpecialImageToDb,
} from '../services/specialsService.ts';

const FIELD = 'w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-800 shadow-sm outline-none transition focus:border-[#ee2b74] focus:ring-2 focus:ring-[#ee2b74]/20';
const BTN   = 'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors';

const DEFAULT_SCHEDULE: SpecialSchedule = {
    recurrence_type: 'weekly', interval_count: 1,
    starts_at: '09:00', ends_at: '17:00',
    timezone: BUSINESS_TIME_ZONE,
    start_date: '', end_date: '',
    days_of_week: [5], day_of_month: null, active: true,
};

interface SpecialModalProps {
    isOpen: boolean;
    editing: Special | null;
    existingSchedules: SpecialSchedule[];
    saving: boolean;
    onClose: () => void;
    onSubmit: (data: {
        title: string;
        imageUrl: string;
        linkType: Special['link_type'];
        linkTarget: string | null;
        homepage: boolean;
        bannerNumber: number;
        expiresAt: string;
        specialMode: SpecialMode;
        schedule: SpecialSchedule;
    }) => Promise<void>;
}

export default function SpecialModal({isOpen, editing, existingSchedules, saving, onClose, onSubmit,}: SpecialModalProps) {
    const [title, setTitle] = useState('');
    const [linkType, setLinkType] = useState<Special['link_type']>('product');
    const [linkTarget, setLinkTarget] = useState<string | null>(null);
    const [imageUrl, setImageUrl] = useState('');
    const [homepage, setHomepage] = useState(false);
    const [bannerNumber, setBannerNumber] = useState(1);
    const [expiresAt, setExpiresAt] = useState('');
    const [specialMode, setSpecialMode]  = useState<SpecialMode>('one_time');
    const [schedule, setSchedule] = useState<SpecialSchedule>(DEFAULT_SCHEDULE);

    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [products, setProducts]  = useState<ProductItem[]>([]);
    const [categories, setCategories]= useState<CategoryItem[]>([]);

    const [bucketOpen, setBucketOpen] = useState(false);
    const [bucketImages, setBucketImages] = useState<StorageImageItem[]>([]);
    const [bucketLoading, setBucketLoading] = useState(false);
    const [bucketOffset, setBucketOffset] = useState(0);
    const [hasMoreImages, setHasMoreImages] = useState(true);
    const [uploading, setUploading] = useState(false);

    const searchRequestIdRef = useRef(0);

    // Populate form when editing changes
    useEffect(() => {
        if (!isOpen) return;
        if (editing) {
            setTitle(editing.title);
            setLinkType(editing.link_type);
            setLinkTarget(editing.link_target ?? null);
            setImageUrl(editing.image_url ?? '');
            setHomepage(editing.homepage);
            setBannerNumber(editing.banner_number ?? 1);
            setExpiresAt(editing.expires_at
                ? new Intl.DateTimeFormat('sv-SE', {
                    timeZone: BUSINESS_TIME_ZONE, year: 'numeric', month: '2-digit',
                    day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
                }).format(new Date(editing.expires_at)).replace(' ', 'T')
                : '');
            const existingSchedule = existingSchedules.find(s => s.special_id === editing.id && s.active);
            if (existingSchedule) {
                setSpecialMode('recurring');
                setSchedule({ ...existingSchedule, start_date: existingSchedule.start_date ?? '', end_date: existingSchedule.end_date ?? '', days_of_week: existingSchedule.days_of_week ?? [] });
            } else {
                setSpecialMode(editing.expires_at ? 'one_time' : 'forever');
                setSchedule(DEFAULT_SCHEDULE);
            }
            if (editing.link_type === 'product') void fetchProductsFromDb().then(setProducts);
            else if (editing.link_type === 'category') void fetchCategoriesFromDb().then(setCategories);
        } else {
            setTitle(''); setLinkType('product'); setLinkTarget(null); setImageUrl('');
            setHomepage(false); setBannerNumber(1); setExpiresAt('');
            setSpecialMode('one_time'); setSchedule(DEFAULT_SCHEDULE);
            void fetchProductsFromDb().then(setProducts);
        }
        setQuery(''); setSearching(false); searchRequestIdRef.current += 1;
    }, [isOpen, editing, existingSchedules]);

    const handleSearch = useCallback(async (q: string) => {
        const clean = q.trim().replace(/\s+/g, ' ');
        if (!clean) {
            setSearching(false);
            if (linkType === 'product') void fetchProductsFromDb().then(setProducts);
            else void fetchCategoriesFromDb().then(setCategories);
            return;
        }
        if (clean.length < MIN_SEARCH_CHARS) return;
        const reqId = ++searchRequestIdRef.current;
        setSearching(true);
        try {
            if (linkType === 'product') {
                const data = await fetchProductsFromDb(clean);
                if (reqId !== searchRequestIdRef.current) return;
                setProducts(data);
            } else {
                const data = await fetchCategoriesFromDb(clean);
                if (reqId !== searchRequestIdRef.current) return;
                setCategories(data);
            }
        } finally {
            if (reqId === searchRequestIdRef.current) setSearching(false);
        }
    }, [linkType]);

    useEffect(() => {
        if (!query) return;
        const timer = window.setTimeout(() => { void handleSearch(query); }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [query, handleSearch]);

    const loadBucketImages = async (reset = false) => {
        setBucketLoading(true);
        const offset = reset ? 0 : bucketOffset;
        try {
            const files = await fetchBucketImagesFromDb(offset);
            setBucketImages(prev => reset ? files : [...prev, ...files]);
            setBucketOffset(offset + files.length);
            setHasMoreImages(files.length === IMAGE_PAGE_SIZE);
        } finally {
            setBucketLoading(false);
        }
    };

    const openBucket = async () => {
        setBucketImages([]); setBucketOffset(0); setHasMoreImages(true);
        setBucketOpen(true);
        await loadBucketImages(true);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.[0]) return;
        setUploading(true);
        try {
            const url = await uploadSpecialImageToDb(e.target.files[0]);
            setImageUrl(url);
        } catch {
            alert('Error uploading image');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit({ title, imageUrl, linkType, linkTarget, homepage, bannerNumber, expiresAt, specialMode, schedule });
    };

    const filteredItems = linkType === 'product' ? products : categories;
    const selectedItem  = filteredItems.find(i => i.id === linkTarget) ?? null;

    return (
        <>
            <Transition show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition show={isOpen} as={Fragment}
                                      enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
                                      leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm" />
                    </Transition>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition show={isOpen} as={Fragment}
                                              enter="ease-out duration-200" enterFrom="opacity-0 translate-y-3 scale-95" enterTo="opacity-100 translate-y-0 scale-100"
                                              leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-3 scale-95">
                                <DialogPanel className="w-full max-w-2xl rounded-2xl bg-white shadow-xl max-h-[90vh] overflow-y-auto">
                                    <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
                                        <div>
                                            <DialogTitle className="text-lg font-semibold text-stone-900">
                                                {editing ? 'Edit Special' : 'Add Special'}
                                            </DialogTitle>
                                            <p className="mt-1 text-sm text-stone-500">Configure how this special should appear and where it should link.</p>
                                        </div>
                                        <button onClick={onClose} className="rounded-lg p-2 text-stone-500 hover:bg-stone-100">
                                            <XMarkIcon className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
                                        {/* Title */}
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold text-stone-700">Title</label>
                                            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                                                   className={FIELD} placeholder="Enter special title" />
                                        </div>

                                        {/* Link */}
                                        <div className="rounded-lg border border-stone-200 bg-stone-50/50 p-3">
                                            <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-stone-900">Link</h3>
                                            <div className="mb-3">
                                                <label className="mb-1.5 block text-xs font-semibold text-stone-700">Type</label>
                                                <div className="relative">
                                                    <select value={linkType}
                                                            onChange={e => {
                                                                const val = e.target.value as Special['link_type'];
                                                                setLinkType(val); setLinkTarget(null); setQuery('');
                                                                searchRequestIdRef.current += 1; setSearching(false);
                                                                if (val === 'product') void fetchProductsFromDb().then(setProducts);
                                                                else if (val === 'category') void fetchCategoriesFromDb().then(setCategories);
                                                            }}
                                                            className={`${FIELD} appearance-none pr-10 w-full`}>
                                                        <option value="product">Product</option>
                                                        <option value="category">Category</option>
                                                        <option value="url">Custom URL</option>
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-stone-400">
                                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                    </div>
                                                </div>
                                            </div>

                                            {linkType === 'url' ? (
                                                <input type="text" value={linkTarget ?? ''} onChange={e => setLinkTarget(e.target.value)}
                                                       className={FIELD} placeholder="https://example.com" />
                                            ) : (
                                                <Combobox value={selectedItem}
                                                          onChange={(item: ProductItem | CategoryItem | null) => {
                                                              setLinkTarget(item?.id ?? null);
                                                              if (linkType === 'product' && item && 'image_url' in item) {
                                                                  setImageUrl(item.image_url ?? '');
                                                              }
                                                          }}>
                                                    <div className="relative">
                                                        <div className="relative">
                                                            <div className="pointer-events-none absolute left-3 top-3 flex items-center">
                                                                {searching
                                                                    ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#ee2b74] border-t-transparent" />
                                                                    : <MagnifyingGlassIcon className="h-5 w-5 text-stone-400" />}
                                                            </div>
                                                            <ComboboxInput
                                                                className={`${FIELD} pl-10 pr-10`}
                                                                displayValue={(item: ProductItem | CategoryItem | null) => item?.name ?? ''}
                                                                onChange={e => setQuery(e.target.value)}
                                                                onFocus={() => { if (!query) void handleSearch(''); }}
                                                                placeholder={`Search ${linkType === 'product' ? 'products' : 'categories'}…`}
                                                            />
                                                            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                                <ChevronUpDownIcon className="h-5 w-5 text-stone-400" />
                                                            </ComboboxButton>
                                                        </div>
                                                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0" afterLeave={() => setQuery('')}>
                                                            <ComboboxOptions className="absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-xl border border-stone-200 bg-white py-1 shadow-lg focus:outline-none">
                                                                {searching ? (
                                                                    <div className="px-4 py-3 text-sm italic text-stone-500">Searching…</div>
                                                                ) : query.trim().length > 0 && query.trim().length < MIN_SEARCH_CHARS ? (
                                                                    <div className="px-4 py-3 text-sm text-stone-500">Type at least {MIN_SEARCH_CHARS} characters to search.</div>
                                                                ) : filteredItems.length === 0 ? (
                                                                    <div className="px-4 py-3 text-sm text-stone-500">No results found.</div>
                                                                ) : (
                                                                    filteredItems.map(item => (
                                                                        <ComboboxOption
                                                                            key={item.id}
                                                                            value={item}
                                                                            className={({ focus }) =>
                                                                                `relative cursor-pointer select-none px-4 py-2.5 pr-10 text-sm ${
                                                                                    focus ? 'bg-pink-50 text-stone-900' : 'text-stone-700'
                                                                                }`
                                                                            }
                                                                        >
                                                                            {({ selected }) => (
                                                                                <>
                                                                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                                                        {item.name}
                                                                                    </span>
                                                                                    {selected && (
                                                                                        <span className="absolute inset-y-0 right-3 flex items-center text-[#ee2b74]">
                                                                                            <CheckIcon className="h-5 w-5" />
                                                                                        </span>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </ComboboxOption>
                                                                    ))
                                                                )}
                                                            </ComboboxOptions>
                                                        </Transition>
                                                    </div>
                                                </Combobox>
                                            )}
                                        </div>

                                        {/* Image */}
                                        <div className="rounded-lg border border-stone-200 bg-stone-50/50 p-3">
                                            <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-stone-900">Image</h3>
                                            <div className="flex items-center gap-3">
                                                {imageUrl ? (
                                                    <div className="relative flex-shrink-0">
                                                        <img src={imageUrl} alt="Preview" className="h-16 w-16 rounded-lg border border-stone-200 object-cover" />
                                                        <div className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#8dc63f] text-white">
                                                            <CheckIcon className="h-3 w-3" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg border border-dashed border-stone-300 bg-stone-100 text-xs text-stone-400">—</div>
                                                )}
                                                <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                                                    <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-stone-300 px-2 py-3 text-center transition-all hover:border-[#ee2b74] hover:bg-pink-50">
                                                        <svg className="h-4 w-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                                        <span className="text-xs font-medium text-stone-600">{uploading ? 'Uploading…' : 'Upload'}</span>
                                                        <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                                                    </label>
                                                    <button type="button" onClick={openBucket}
                                                            className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-stone-300 bg-white px-2 py-3 text-xs font-medium text-stone-700 transition-all hover:border-stone-400 hover:bg-stone-50">
                                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                        Choose
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Special type */}
                                        <div className="rounded-lg border border-stone-200 bg-stone-50/50 p-3">
                                            <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-stone-900">Special Type</h3>
                                            <div className="space-y-2">
                                                {([
                                                    { value: 'one_time',  label: 'One-time special',  desc: 'Runs until an expiry date/time.' },
                                                    { value: 'forever',   label: 'Forever',            desc: 'No expiry date. Stays until you remove it.' },
                                                    { value: 'recurring', label: 'Recurring special',  desc: 'Repeats weekly, daily, or monthly on a schedule.' },
                                                ] as const).map(opt => (
                                                    <label key={opt.value} className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 bg-white p-3 transition hover:border-[#ee2b74]/40">
                                                        <input type="radio" name="specialMode" checked={specialMode === opt.value}
                                                               onChange={() => { setSpecialMode(opt.value); if (opt.value === 'forever') setExpiresAt(''); }}
                                                               className="mt-1 h-4 w-4 accent-[#ee2b74]" />
                                                        <div>
                                                            <p className="text-sm font-medium text-stone-900">{opt.label}</p>
                                                            <p className="text-xs text-stone-500">{opt.desc}</p>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Expires At */}
                                        {specialMode === 'one_time' && (
                                            <div>
                                                <label className="mb-1.5 block text-xs font-semibold text-stone-700">Expires At</label>
                                                <input type="datetime-local" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} className={FIELD} />
                                                <p className="mt-1 text-xs text-stone-500">Leave blank if you want this special to never end.</p>
                                            </div>
                                        )}

                                        {specialMode === 'forever' && (
                                            <div className="rounded-lg border border-[#c5e08a] bg-[#f0f7e6] px-3 py-2 text-sm text-[#5a7f28]">
                                                This special will never expire.
                                            </div>
                                        )}

                                        {specialMode === 'recurring' && (
                                            <ScheduleSection schedule={schedule}
                                                             onChange={patch => setSchedule(prev => ({ ...prev, ...patch }))} />
                                        )}

                                        {/* Display */}
                                        <div className="rounded-lg border border-stone-200 bg-stone-50/50 p-3">
                                            <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-stone-900">Display</h3>
                                            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg p-2 transition hover:bg-stone-100/50">
                                                <input type="checkbox" checked={homepage} onChange={e => setHomepage(e.target.checked)}
                                                       className="h-4 w-4 rounded border-stone-300 accent-[#ee2b74]" />
                                                <span className="text-sm font-medium text-stone-900">Show on homepage</span>
                                            </label>
                                            {homepage && (
                                                <div className="mt-2 rounded-lg border border-pink-200">
                                                    <div className="relative">
                                                        <select value={bannerNumber} onChange={e => setBannerNumber(Number(e.target.value))}
                                                                className={`${FIELD} appearance-none pr-8 text-xs w-full`}>
                                                            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>Banner {n}</option>)}
                                                        </select>
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-stone-500">
                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Footer */}
                                        <div className="flex items-center justify-end gap-2.5 border-t border-stone-200 pt-4">
                                            <button type="button" onClick={onClose}
                                                    className={`${BTN} border border-stone-300 bg-white text-stone-700 hover:bg-stone-50`}>
                                                Cancel
                                            </button>
                                            <button type="submit" disabled={saving}
                                                    className={`${BTN} bg-[#ee2b74] text-white hover:bg-[#d4245f] disabled:opacity-50`}>
                                                {saving ? (editing ? 'Saving…' : 'Creating…') : (editing ? 'Save Changes' : 'Create Special')}
                                            </button>
                                        </div>
                                    </form>
                                </DialogPanel>
                            </Transition>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <ImagePickerModal
                isOpen={bucketOpen}
                onClose={() => setBucketOpen(false)}
                onSelect={url => setImageUrl(url)}
                images={bucketImages}
                loading={bucketLoading}
                hasMore={hasMoreImages}
                onLoadMore={() => void loadBucketImages(false)}
            />
        </>
    );
}